import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip,
} from "@mui/material";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  LocationCity as LocationCityIcon,
  PinDrop as PinDropIcon,
  Public as PublicIcon,
  Work as WorkIcon,
  Build as BuildIcon,
} from "@mui/icons-material";
import DesktopBanner from "../../assets/ClientBanner.jpg";
import axios from "axios";
import { BASE_URL } from "../../components/config/config";

// ✅ Reusable InfoRow with Tailwind for spacing + typography
const InfoRow = ({ icon: Icon, label, value }) => (
  <Stack direction="row" spacing={1.2} alignItems="center" className="my-1">
    {Icon && <Icon fontSize="small" className="text-gray-500" />}
    <Typography variant="body2" className="font-semibold min-w-[110px]">
      {label}
    </Typography>
    <Typography variant="body2" className="text-gray-500 break-words">
      {value || "Not specified"}
    </Typography>
  </Stack>
);

// ✅ ProfileCard with Tailwind + MUI
const ProfileCard = ({ title, icon: Icon, children }) => (
  <Card className="rounded-2xl shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 bg-white">
    <Box className="px-4 py-3 flex items-center gap-2 bg-gradient-to-r from-teal-600 to-teal-800 text-white">
      {Icon && <Icon />}
      <Typography variant="h6" className="font-semibold">
        {title}
      </Typography>
    </Box>
    <CardContent>{children}</CardContent>
  </Card>
);

// ✅ ServiceCategoryTable with Tailwind enhancements
const ServiceCategoryTable = ({ clientId }) => {
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return setLoading(false);
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}client/get-client-report/${clientId}`
        );
        if (res.data?.success) setClientData(res.data.data);
      } catch {
        setClientData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  if (loading)
    return (
      <Box className="flex justify-center py-6">
        <CircularProgress size={50} color="primary" />
      </Box>
    );

  const categoryDetails =
    clientData?.client_details?.serviceAreas?.category_details || [];

  return (
    <Paper className="mt-6 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
      <Box className="p-4 bg-gradient-to-r from-teal-600 to-teal-800 text-white">
        <Typography variant="h5" className="font-semibold">
          Service Categories
        </Typography>
        <Typography variant="body2" className="opacity-80">
          Payment methods and pricing for each service
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="bg-gray-50">
              <TableCell className="font-bold">Category</TableCell>
              <TableCell className="font-bold">Price</TableCell>
              <TableCell className="font-bold">Payment Method</TableCell>
              <TableCell className="font-bold">Booking Price (QR)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categoryDetails.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" className="py-6">
                  <Typography className="text-gray-500">
                    No categories available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              categoryDetails.map((cat) => {
                const selectedValue = cat.payment_way?.isPayViaCustomer
                  ? "customer"
                  : cat.payment_way?.isPayViaClient
                  ? "client"
                  : "";
                return (
                  <TableRow
                    key={cat.category_id}
                    hover
                    className="hover:bg-gray-100 cursor-pointer"
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <BuildIcon fontSize="small" className="text-teal-600" />
                        <Typography>{cat.label}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`₹${cat.category_service_prize || 0}`}
                        className="bg-teal-50 text-teal-700 border border-teal-300"
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl>
                        <RadioGroup row value={selectedValue}>
                          <FormControlLabel
                            value="customer"
                            control={<Radio disabled />}
                            label="Customer"
                          />
                          <FormControlLabel
                            value="client"
                            control={<Radio disabled />}
                            label="Client"
                          />
                        </RadioGroup>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`₹${cat?.payment_way?.ticketPicPrize || 0}`}
                        className="bg-teal-50 text-teal-700 border border-teal-300"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// ✅ Dynamic Banner with rounded corners
const DynamicBanner = ({ qrId }) => {
  const [banner, setBanner] = useState(null);
  useEffect(() => {
    if (!qrId) return;
    axios
      .get(`${BASE_URL}admin/get-common-banner`, {
        params: { isActive: true, qr_id: qrId },
      })
      .then((res) => setBanner(res.data?.data?.[0] || null))
      .catch(() => setBanner(null));
  }, [qrId]);

  const bannerImage = banner?.image || "";

  return (
    <Box
      className="relative h-[200px] md:h-[260px] xl:h-[340px] rounded-2xl overflow-hidden shadow-lg"
      sx={{
        backgroundImage: `url(${bannerImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Box className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40 rounded-2xl" />
    </Box>
  );
};

// ✅ Main Component
export default function ClientProfile() {
  const { clientDetails = {}, centerDetailsPopulated = {} } = useSelector(
    (state) => state.clientReducer ?? {}
  );
  const personal = clientDetails.client_poc ?? {};
  const address = clientDetails.address_details ?? {};
  const qrId = clientDetails?.qr_details?.qr_id || centerDetailsPopulated.qr_id;

  return (
    <Box>
      {/* Banner */}
      <DynamicBanner qrId={qrId} />

      {/* Content */}
      <Box className="pt-8 pb-6">
        <Grid container spacing={3} className="justify-evenly items-stretch">
          <Grid item xs={12} md={4} className="flex">
            <ProfileCard title="Contact Details" icon={PersonIcon}>
              <Stack spacing={2}>
                <InfoRow
                  icon={PhoneIcon}
                  label="Phone:"
                  value={personal?.phone?.mobile_number}
                />
                <InfoRow
                  icon={PersonIcon}
                  label="Name:"
                  value={personal?.person_name}
                />
                <InfoRow
                  icon={EmailIcon}
                  label="Email:"
                  value={clientDetails.official_email}
                />
              </Stack>
            </ProfileCard>
          </Grid>

          <Grid item xs={12} md={4} className="flex">
            <ProfileCard title="Address" icon={HomeIcon}>
              <Stack spacing={2}>
                <InfoRow
                  icon={LocationCityIcon}
                  label="Locality:"
                  value={address.address_line}
                />
                <InfoRow
                  icon={PinDropIcon}
                  label="Pincode:"
                  value={address.pincode}
                />
                <InfoRow
                  icon={LocationCityIcon}
                  label="City:"
                  value={address.city}
                />
                <InfoRow
                  icon={LocationCityIcon}
                  label="State:"
                  value={address.state}
                />
                <InfoRow
                  icon={PublicIcon}
                  label="Country:"
                  value={address.country}
                />
              </Stack>
            </ProfileCard>
          </Grid>

          <Grid item xs={12} md={4} className="flex">
            <ProfileCard title="Services" icon={BuildIcon}>
              <Stack spacing={2}>
                <InfoRow
                  icon={WorkIcon}
                  label="Category:"
                  value={clientDetails.category}
                />
                <Divider />
                <InfoRow
                  icon={WorkIcon}
                  label="Product:"
                  value={clientDetails.product_name}
                />
                <InfoRow
                  icon={WorkIcon}
                  label="Service Info:"
                  value={clientDetails.specific_requirement}
                />
              </Stack>
            </ProfileCard>
          </Grid>
        </Grid>

        {clientDetails._id && (
          <ServiceCategoryTable clientId={clientDetails._id} />
        )}
      </Box>
    </Box>
  );
}
